/**
 * @fileoverview A plant's week as a grid, a key and the week's items. B136: it is off the plant card (the weather is one caption
 * line there, WeekCaption); it shows inside the opened caption ("... more").
 *
 * Top to bottom: the week as a grid (WeekGrid), a one-line key to what is on
 * it this week, then the week's items, each a title and one paragraph.
 * Everything is open: the fold itself is the one the gardener chose to
 * open, and a second fold per item was a second tap for one reason. The
 * plant's Care is its own strip on the card (CareStrip).
 *
 * Everything is said in the words of the plant's temperature scale (the
 * About sheet): grows best, grows slowly, chilly nights, frost. The grid
 * marks a reading by what it means: plain where it grows best (give or take
 * a few degrees), a tint where it grows slowly, an outline for harm (a
 * chilling night, a sudden cold snap or heat spike), solid for a danger
 * (frost, freeze, past what it survives, wind). A rain row appears when the
 * week has a rain day, a downpour outlined. Rain and water are in inches, or
 * in millimetres for a gardener reading °C (formatRain). The key lists only
 * what the grid shows this week.
 *
 * What the week means is the server's, all of it (D40; audit batch 4, A23,
 * A25, A26: the app judged its own paragraphs, tint and rain row, and graded
 * the week itself from the bare forecast when the outlook had no row): the
 * plants brief (usePlantsBrief, GET /plants/brief) sends each mark's colour
 * (its token, harm's included: the app maps no reason to a colour, batch 13a,
 * A83) and words, which way each slow half leans (week.slow[].lean), the range it
 * grows best in for the key (week.best), the items under the grid with their
 * paragraphs (week.items: dangers with the act tag, rain, harm, "Grows
 * slowly", Water last; services/plants/plantWeekItems), what counts as rain
 * on the rain row (rainShownInches, the one rain-day Grow setting) and the
 * garden's day (date, for the "Today" column). The outlook (usePlantOutlook,
 * GET /plants/outlook) gives the grid its readings and days. What stays here
 * is laying them out.
 *
 * A card is only given a week when the brief has one (PlantCard's week). A
 * week with no outlook row to draw reads the outlook again once, quietly (B28,
 * lib/outlookRow), and shows no grid and no words until the row arrives; the
 * standard failure sentence (D31, reported to Sentry, the support address as a
 * link) is only for an outlook that failed on our side. Never a week graded
 * here from the bare forecast.
 *
 * Where: WeekCaption, under the opened story.
 * API: none of its own: the outlook (usePlantOutlook) and the plants brief
 * (usePlantsBrief).
 * Settings: the gardener's unit (Profile).
 * Words (en/es): plants.card.* (forecastLoading, grid.*, week.key.*, act);
 * temperature.forecast.noRain; serverFailure.message (through
 * lib/serverFailure). The items' titles and paragraphs are the brief's.
 *
 * @module TemperatureForecast
 */

import React from 'react';
import { Thermometer } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useUserConfig } from '@/shared/context/userConfigStore';
import type { OutlookDay } from '@/shared/api/outlook';
import { usePlantOutlook } from '@/shared/hooks/usePlantOutlook';
import { formatTemperature } from '@/shared/utils/temperatureUtils';
import { formatRain } from '@/shared/utils/rainUtils';
import { TOKEN_BORDER, TOKEN_SOLID, TOKEN_TEXT, TOKEN_TINT, type VerdictToken } from '@/shared/theme/verdictTokens';
import { WeekGrid, type CellFill, type GridCell, type GridDay } from './card/WeekGrid';
import { usePlantsBrief } from '@/shared/hooks/usePlantsBrief';
import type { BriefMark } from '@/shared/api/plantsBrief';
import { TooltipProvider } from '@/shared/context/TooltipContext';
import { isServerFailure, serverFailureText, serverFailureWords } from '@/shared/lib/serverFailure';
import { outlookRowState } from '../lib/outlookRow';
import { FailureText } from '@/shared/components/ServerFailure';

/** What the panel needs: the plant, from which its outlook and its week said are found. */
interface TemperatureForecastProps {
  /** The plant whose week this is. */
  plantObjId: number;
}

/** Harm by temperature, by the grading's reason. */
type HarmBasis = NonNullable<BriefMark['basis']>;

/**
 * A number of inches as the outlook sends it ("1.5"), or 0.
 *
 * @param value - A number or its text; anything unreadable is 0.
 */
const inchesOf = (value: string | number | null | undefined): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Whether a half of the week is one of a mark's.
 *
 * @param mark - The mark.
 * @param date - The day, "YYYY-MM-DD".
 * @param when - Its day or night.
 */
const markHas = (mark: BriefMark, date: string, when: 'day' | 'night'): boolean =>
  mark.halves.some((h) => h.date === date && h.when === when);

/**
 * The standard failure sentence for an outlook that failed on our side,
 * reported to Sentry once (B28: only a real failure, never a missing row or
 * a refusal).
 *
 * @param props.error - The outlook's failure.
 */
const NoRow: React.FC<{ error: unknown }> = ({ error }) => {
  const text = React.useMemo(
    () => serverFailureText(error, 'temperatureForecast') ?? serverFailureWords(error),
    [error],
  );
  return (
    <div className="w-full pt-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-medium text-destructive">
        <span className="flex items-center gap-1.5">
          <Thermometer className="h-4 w-4 shrink-0" />
          <span><FailureText text={text} /></span>
        </span>
      </div>
    </div>
  );
};

/**
 * The week's grid and key, then its items.
 *
 * @param props - TemperatureForecastProps.
 */
export const TemperatureForecast: React.FC<TemperatureForecastProps> = ({ plantObjId }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { forPlant: outlookForPlant, isFetching: outlookFetching, conditions, error: outlookError, refetch: refetchOutlook, updatedAt: outlookStamp } = usePlantOutlook();
  /** This plant's week from the garden's outlook (GET /plants/outlook, through usePlantOutlook): the grid's readings. */
  const outlook = outlookForPlant(plantObjId);
  const { forPlant: briefFor, isFetching: briefFetching, brief } = usePlantsBrief();
  /** This plant's week as the plants brief says it (its marks, leans, items and best range; D40, A26). */
  const said = briefFor(plantObjId)?.week ?? null;
  /*
   * A plant added moments ago has no outlook row until the refetch lands, and
   * the items are the brief's: the week waits for both rather than drawing
   * half of it.
   */
  const awaiting = (!outlook && outlookFetching) || (!said && briefFetching);
  /** The plant, and the outlook state (its arrival time), on which this card asked for a missing row to be read again (B28, C55: the hook reads once for all cards, and a later outlook may be asked again). */
  const [refetchedFor, setRefetchedFor] = React.useState<{ plantObjId: number; stamp: number } | null>(null);
  /** What to do about the outlook row (B28). */
  const rowState = outlookRowState({
    hasOutlook: !!outlook,
    hasWeek: !!said,
    fetching: outlookFetching || briefFetching,
    serverFailure: isServerFailure(outlookError),
    refetched: refetchedFor?.plantObjId === plantObjId && refetchedFor.stamp === outlookStamp,
  });
  React.useEffect(() => {
    if (rowState !== 'refetch') return;
    setRefetchedFor({ plantObjId, stamp: outlookStamp });
    refetchOutlook();
  }, [rowState, plantObjId, outlookStamp, refetchOutlook]);
  const { userConfig } = useUserConfig();
  /** The gardener's unit, F or C (Profile). */
  const unit = userConfig.preferences.temperatureUnit;

  /**
   * The week's readings by date, in Fahrenheit whatever the outlook was
   * drawn in, so a cell is said in the gardener's unit from one scale.
   */
  const readings = React.useMemo(() => {
    /**
     * A reading in Fahrenheit, from the outlook's unit.
     *
     * @param value - The reading, or null.
     */
    const toF = (value: number | null): number | null => (value === null ? null
      : conditions?.tempUnit === 'c' ? Math.round(value * 9 / 5 + 32) : value);
    return new Map((conditions?.days ?? []).map((day) => [day.date, { min: toF(day.min), max: toF(day.max) }]));
  }, [conditions]);

  if (awaiting || rowState === 'refetch') {
    return (
      <div className="w-full pt-1">
        <div className="flex items-center gap-1.5 font-medium">
          <Thermometer className="h-4 w-4 text-destructive" />
          <span>{t('plants.card.forecastLoading')}</span>
        </div>
      </div>
    );
  }

  // A week with no outlook row to draw it from (A23, B28): never graded here. A failure on our side is worded and reported; anything else shows nothing.
  if (!outlook || !said) return rowState === 'failure' ? <NoRow error={outlookError} /> : null;

  /**
   * A Fahrenheit reading in the gardener's unit.
   *
   * @param tempF - The reading.
   */
  const deg = (tempF: number): string => formatTemperature(tempF, unit);
  /**
   * The same without its unit letter, "52°", for a grid cell.
   *
   * @param tempF - The reading.
   */
  const bare = (tempF: number): string => deg(tempF).replace(/[FC]$/, '');
  /**
   * A date's weekday in the reader's language, short or long.
   *
   * @param date - YYYY-MM-DD.
   * @param width - "Tue" or "Tuesday".
   */
  const weekdayOf = (date: string, width: 'short' | 'long'): string =>
    new Intl.DateTimeFormat(language, { weekday: width }).format(new Date(`${date}T12:00:00`));
  /**
   * An amount of rain from the outlook's inches, in the gardener's unit: "1.5″", or "38 mm" for °C (formatRain).
   *
   * @param value - Inches, as the outlook sends them (a number or a string).
   */
  const inches = (value: string | number | null | undefined): string => formatRain(inchesOf(value), unit);

  /** The week's days, today first. */
  const daily = outlook.daily ?? [];
  /** The garden's day, the brief's: the first column is "Today" only when it is that day. */
  const today = brief?.date ?? null;
  /** Rain from which a day is marked on the rain row: a rain day, the server's (A25); none until the brief is in. */
  const rainShown = brief?.rainShownInches ?? Number.POSITIVE_INFINITY;
  const marks = said.marks;
  /** Frost, freeze, past what it survives, wind. */
  const dangers = marks.filter((mark) => mark.kind === 'danger');
  /** Chilling, a cold snap, a heat spike. */
  const harms = marks.filter((mark) => mark.kind === 'harm' && mark.basis);
  /** Too much rain or a downpour, or null. */
  const rainMark = marks.find((mark) => mark.family === 'downpour' || mark.family === 'excess_rain') ?? null;
  /** The half-days that only slow it, as "date|when", with the way each leans (the server's). */
  const slowLean = new Map(said.slow.map((h) => [`${h.date}|${h.when}`, h.lean]));
  /**
   * A day's rain, in inches.
   *
   * @param day - The day.
   */
  const rainOn = (day: OutlookDay): number => inchesOf(day.rainInches);
  /**
   * Whether a day is one of the week's marked downpours.
   *
   * @param day - The day.
   */
  const isDownpour = (day: OutlookDay): boolean => rainMark?.family === 'downpour' && rainMark.dates.includes(day.date);
  /** The rain row shows only in a week with a rain day. */
  const showRain = daily.some((day) => rainOn(day) >= rainShown);

  /**
   * A half's reading in Fahrenheit, or null.
   *
   * @param day - The day.
   * @param when - Its high (day) or its low (night).
   */
  const readingOf = (day: OutlookDay, when: 'day' | 'night'): number | null => {
    const reading = readings.get(day.date);
    const raw = when === 'day' ? reading?.max : reading?.min;
    return typeof raw === 'number' ? raw : null;
  };

  /** Every half-day with what its marks say: a danger (one with a colour before wind), harm, or growing slowly. */
  const halves = daily.flatMap((day) => (['day', 'night'] as const).map((when) => {
    const value = readingOf(day, when);
    const dangerMarks = dangers.filter((mark) => markHas(mark, day.date, when));
    const danger = dangerMarks.find((mark) => mark.token !== null) ?? dangerMarks[0] ?? null;
    const harmMark = danger ? null : harms.find((mark) => markHas(mark, day.date, when)) ?? null;
    const harm = harmMark?.basis ?? null;
    const key = `${day.date}|${when}`;
    const lean = !danger && !harm && slowLean.has(key) ? slowLean.get(key) ?? null : null;
    // Harm's colour is its mark's, as the server sends it.
    return { day, when, value, danger, harm, harmToken: harmMark?.token ?? null, lean };
  }));
  /** One half-day as the grid and the key read it. */
  type Half = (typeof halves)[number];

  /**
   * A half-day as a grid cell, marked by what it means.
   *
   * @param h - The half-day.
   * @returns The cell, or null where there is no reading and no danger.
   */
  const halfCell = (h: Half): GridCell | null => {
    if (h.value === null && !h.danger) return null;
    const name = t(h.when === 'day' ? 'plants.card.grid.dayLabel' : 'plants.card.grid.nightLabel', { day: weekdayOf(h.day.date, 'long') });
    let fill: CellFill = 'plain';
    let token: VerdictToken | null = null;
    let meaning: string | null = null;
    if (h.danger) {
      fill = 'solid';
      // Wind leans neither way: solid in the ink, without a colour of its own.
      token = h.danger.token;
      meaning = h.danger.what;
    } else if (h.harm) {
      fill = 'harm';
      token = h.harmToken;
      meaning = t(`plants.card.week.key.${h.harm}`);
    } else if (h.lean && h.value !== null) {
      fill = 'tint';
      token = h.lean;
      meaning = t('plants.card.week.key.slow');
    }
    return {
      text: h.value !== null ? bare(h.value) : null,
      token,
      fill,
      label: [name, h.value !== null ? deg(h.value) : null, meaning].filter(Boolean).join(', '),
    };
  };

  /**
   * A day's rain as a grid cell: its amount, a marked downpour outlined with a drop.
   *
   * @param day - The day.
   */
  const rainCell = (day: OutlookDay): GridCell => {
    const amount = rainOn(day);
    const downpour = isDownpour(day);
    return {
      text: amount >= rainShown ? formatRain(amount, unit, { compact: true, inchDigits: 1, fixed: true }) : null,
      token: 'water',
      fill: downpour ? 'harm' : 'plain',
      drop: downpour,
      label: [weekdayOf(day.date, 'long'), amount >= rainShown ? inches(amount) : t('temperature.forecast.noRain'), downpour ? t('plants.card.week.key.downpour') : null]
        .filter(Boolean).join(', '),
    };
  };

  /** The grid's columns, today first. */
  const gridDays: GridDay[] = daily.map((day, i) => ({
    date: day.date,
    name: i === 0 && day.date === today ? t('plants.card.grid.today') : weekdayOf(day.date, 'short'),
    day: halfCell(halves[i * 2]),
    night: halfCell(halves[i * 2 + 1]),
    rain: rainCell(day),
  }));

  /** The items under the grid, the brief's, each in its colour; a danger with the act tag. */
  const items = said.items.map((item) => ({
    ...item,
    tone: item.token ? TOKEN_TEXT[item.token] : 'text-foreground',
    actLook: item.act ? (item.token ? TOKEN_SOLID[item.token] : 'bg-foreground text-background') : null,
  }));

  /*
   * The key: where it grows best, then only what the grid shows this week,
   * each with a swatch drawn the way its cells are.
   */
  const cells = gridDays.flatMap((d) => [d.day, d.night]).filter((c): c is GridCell => c !== null);
  /** The colours "grows slowly" is drawn in this week: cool, warm or both. */
  const tintTokens = [...new Set(cells.filter((c) => c.fill === 'tint' && c.token).map((c) => c.token as VerdictToken))];
  /** The key's entries, each a swatch and its words. */
  const keyParts: Array<{ key: string; swatch: string[]; text: string }> = [];
  if (tintTokens.length > 0) keyParts.push({ key: 'slow', swatch: tintTokens.map((token) => TOKEN_TINT[token]), text: t('plants.card.week.key.slow') });
  for (const mark of harms) {
    const basis = mark.basis as HarmBasis;
    if (halves.some((h) => h.harm === basis)) {
      const swatch = mark.token ? cn(TOKEN_TINT[mark.token], 'border-2', TOKEN_BORDER[mark.token]) : 'border-2 border-foreground';
      keyParts.push({ key: basis, swatch: [swatch], text: t(`plants.card.week.key.${basis}`) });
    }
  }
  for (const mark of dangers.filter((m) => halves.some((h) => h.danger === m))) {
    const text = mark.what;
    if (!keyParts.some((p) => p.text === text)) keyParts.push({ key: mark.family, swatch: [mark.token ? TOKEN_SOLID[mark.token] : 'bg-foreground'], text });
  }
  if (showRain && daily.some(isDownpour)) {
    keyParts.push({ key: 'downpour', swatch: [cn(TOKEN_TINT.water, 'border-2', TOKEN_BORDER.water)], text: t('plants.card.week.key.downpour') });
  }

  /** "Grows best {best}, give or take a few", split around the brief's range so it can be bold. */
  const best = said.best;
  const bestWords = best ? t('plants.card.week.key.best', { best: '{best}' }).split('{best}') : null;

  return (
    <TooltipProvider>
      {/* Flat on the card: a white box inside the cream card read as a second card. */}
      <div className="w-full pt-1">
        <div className="space-y-3 font-sans">
          <WeekGrid days={gridDays} showRain={showRain} />

          {/* Only what the grid shows this week, after where it grows best. */}
          <p className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[0.78rem] text-muted-foreground">
            {bestWords && best && (
              <span>
                {bestWords[0]}
                <b className="font-semibold text-foreground">{best}</b>
                {bestWords[1]}
              </span>
            )}
            {keyParts.map((part) => (
              <span key={part.key} className="inline-flex items-center gap-1">
                {/* One swatch, split where it stands for both sides (grows slowly, cool or warm). */}
                <span className="inline-flex h-3 w-4 overflow-hidden rounded-sm" aria-hidden="true">
                  {part.swatch.map((look, i) => <span key={i} className={cn('h-full flex-1', look)} />)}
                </span>
                {part.text}
              </span>
            ))}
          </p>

          {/* The items: a title and one paragraph each, all open, one left edge. */}
          {items.length > 0 && (
            <ul className="space-y-2.5 border-t border-border pt-2.5">
              {items.map((item) => (
                <li key={item.key} className="text-sm leading-snug">
                  <p className={cn('flex flex-wrap items-center gap-x-1.5 font-semibold', item.tone)}>
                    {item.title}
                    {item.actLook && (
                      <span className={cn('rounded-sm px-1.5 py-px text-[0.625rem] font-bold uppercase tracking-wider', item.actLook)}>
                        {t('plants.card.act')}
                      </span>
                    )}
                  </p>
                  <p className="text-foreground">{item.text}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};
